import './installer-finder.scss';
import SimpleBar from 'simplebar';

/**
 * Class representing the Installer Finder component.
 * Handles responsive layout adjustments, AJAX form submissions,
 * scrollable results, and dynamic image height adjustments.
 */
class InstallerFinder {
    /**
     * Creates an instance of InstallerFinder.
     * @param {HTMLElement} element - The root element of the Installer Finder component.
     */
    constructor(element) {
        /** @type {HTMLElement} Root element of the component */
        this.$installerFinder = element;

        /** @type {HTMLElement|null} Results container element */
        this.$installerFinderResults = this.$installerFinder.querySelector('[data-installer-finder="results"]');

        /** @type {HTMLElement|null} Image element for height calculations */
        this.$installerFinderImage = this.$installerFinder.querySelector('[data-installer-finder="image"]');

        /** @type {HTMLElement|null} Form element inside the root */
        this.$installerFinderForm = this.$installerFinder.querySelector('[data-installer-finder="form"]');

        /**
         * Media query handler for desktop breakpoints
         * @type {Object<string, MediaQueryList>}
         */
        this.mediaMatch = window.mediaMatchHandler ?? {1024: window.matchMedia?.('(min-width: 1024px)')};

        /**
         * SimpleBar instance for custom scrollbars
         * @type {SimpleBar|null}
         */
        this.simplebar = this.$installerFinderResults ? new SimpleBar(this.$installerFinderResults, {autoHide: false}) : null;

        /**
         * ResizeObserver instance for dynamic image height adjustments
         * @type {ResizeObserver|null}
         */
        this.imageResizeObserver = null;
    }

    /**
     * Initializes the Installer Finder by setting up AJAX, media queries, image observer, and results height.
     * @public
     */
    initialize() {
        this.setupAjax();
        this.setupMediaListeners();
        this.setupImageObserver();
        this.applyResultsHeight();
        this.simplebar?.recalculate();
    }

    /**
     * Sets up a ResizeObserver (or fallback listeners) for the image element
     * to dynamically adjust the results container height.
     * @private
     */
    setupImageObserver() {
        if (!this.$installerFinderImage) return;

        if ('ResizeObserver' in window) {
            this.imageResizeObserver = new ResizeObserver(() => this.applyResultsHeight());
            this.imageResizeObserver.observe(this.$installerFinderImage);
        } else {
            this.$installerFinderImage.addEventListener('load', () => this.applyResultsHeight(), {once: true});
            window.addEventListener('resize', () => this.applyResultsHeight());
        }

        if (this.$installerFinderImage.tagName === 'IMG') {
            this.$installerFinderImage.addEventListener('load', () => this.applyResultsHeight());
        }
    }

    /**
     * Sets up media query listeners to update results height on viewport changes.
     * @private
     */
    setupMediaListeners() {
        const mq = this.mediaMatch?.[1024];
        if (!mq) return;

        const callback = () => this.applyResultsHeight();
        mq.addEventListener?.('change', callback);
    }

    /**
     * Calculates and applies the appropriate height to the results container
     * depending on the viewport size and image height.
     * @private
     */
    applyResultsHeight() {
        if (!this.$installerFinderResults) return;

        const isDesktop = !!this.mediaMatch?.[1024]?.matches;

        if (!isDesktop) {
            this.$installerFinderResults.style.height = 'auto';
            this.simplebar?.recalculate();
            return;
        }

        if (!this.$installerFinderImage) return;

        const rawHeight = this.$installerFinderImage.offsetHeight - 122;
        const clamped = Math.max(0, rawHeight | 0);

        this.$installerFinderResults.style.height = `${clamped}px`;
        this.simplebar?.recalculate();
    }

    /**
     * Disconnects observers and cleans up event listeners.
     * @public
     */
    destroy() {
        this.imageResizeObserver?.disconnect?.();
    }

    /**
     * Sets up AJAX form submission with automatic content replacement and re-initialization.
     * @private
     */
    setupAjax() {
        if (!this.$installerFinderForm) return;

        /** @type {HTMLElement|null} Optional loader/spinner element */
        this.$installerFinderLoader = this.$installerFinder.querySelector('[data-installer-finder="loader"]');

        this.$installerFinderForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            this.showSpinner();
            const formData = new FormData(this.$installerFinderForm);

            try {
                const response = await fetch(this.$installerFinderForm.action, {
                    method: this.$installerFinderForm.method || 'POST',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'application/json',
                    },
                    body: formData,
                });

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const data = await response.json();

                if (data.action === 'render' && data.content) {
                    const $resultContainer = this.$installerFinder.querySelector('[data-installer-finder="content"]');

                    if ($resultContainer) {
                        $resultContainer.innerHTML = data.content;
                        this.hideSpinner();

                        const newFinder = new InstallerFinder(this.$installerFinder);
                        newFinder.initialize();

                        const $$formFields = this.$installerFinder.querySelectorAll('[data-form-field]');
                        if ($$formFields.length > 0) {
                            import('./../../components/form/form-field').then(({FormField}) => {
                                for (let i = 0; i < $$formFields.length; i++) {
                                    const $formField = $$formFields[i];
                                    $formField.API = new FormField($formField);
                                }
                            });
                        }

                        this.$installerFinder.querySelector('[data-installer-finder="input"]')?.focus();
                    }
                }
            } catch (error) {
                console.error('InstallerFinder AJAX Error:', error);
            }
        });
    }

    /**
     * Hides the optional loader/spinner element.
     * @public
     */
    hideSpinner() {
        if (this.$installerFinderLoader) this.$installerFinderLoader.hidden = true;
    }

    /**
     * Shows the optional loader/spinner element and hides it on page cache restore.
     * @public
     */
    showSpinner() {
        if (this.$installerFinderLoader) this.$installerFinderLoader.hidden = false;

        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                if (this.$installerFinderLoader) this.$installerFinderLoader.hidden = true;
            }
        });
    }
}

export {InstallerFinder};

window.addEventListener('content.loaded', (e) => {
    const $context = e.detail.$context;

    if ($context) {
        const $installerFinder = $context.querySelectorAll('[data-installer-finder="root"]');

        $installerFinder.forEach((el) => {
            const installerFinder = new InstallerFinder(el);
            installerFinder.initialize();
        });
    }
});
